import { motion, AnimatePresence } from "framer-motion";
import { 
  MapPin, Check, X, Eye, ChevronDown, ChevronUp, Search, RefreshCw, 
  Trash2, FileText, Loader2, Star, AlertCircle
} from "lucide-react";

export default function AdminReviewsTab({
  reviewVenues,
  filteredReviewVenues,
  reviewsTotal,
  reviewFilters,
  setReviewFilters,
  reviewsLoading,
  expandedVenues,
  setExpandedVenues,
  searchQuery,
  setSearchQuery,
  fetchReviews,
  handleReviewStatusChange,
  handleDeleteReview,
  pendingSubmissionReviews = [],
  approveSubmissionReview,
  rejectSubmissionReview
}) {
  return (
    <div className="space-y-4">
      {/* Pending Submission Reviews Alert */}
      {pendingSubmissionReviews.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            <h3 className="font-semibold text-amber-800">
              {pendingSubmissionReviews.length} Review{pendingSubmissionReviews.length > 1 ? 's' : ''} from Venue Submissions
            </h3>
          </div>
          <p className="text-sm text-amber-700 mb-4">
            These reviews were submitted with venue additions and need separate verification.
          </p>
          <div className="space-y-3">
            {pendingSubmissionReviews.map((review) => (
              <div key={review.id} className="bg-white rounded-lg p-4 border border-amber-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900">{review.reviewer_name}</span>
                      <span className="text-gray-400">•</span>
                      <span className="text-sm text-gray-500">{review.venue_name}</span>
                      {review.venue_city && (
                        <>
                          <span className="text-gray-400">•</span>
                          <span className="text-xs text-gray-400">{review.venue_city}</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-1 mb-2">
                      {[...Array(10)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${i < review.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`}
                        />
                      ))}
                      <span className="ml-2 text-sm font-medium text-gray-700">{review.rating}/10</span>
                    </div>
                    {review.comment && (
                      <p className="text-sm text-gray-600 bg-gray-50 rounded p-2 italic">
                        "{review.comment}"
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-2">
                      Submitted: {new Date(review.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 mt-3 sm:mt-0 sm:ml-4">
                    <button
                      onClick={() => approveSubmissionReview(review.id)}
                      className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center justify-center gap-1"
                      data-testid={`approve-submission-review-${review.id}`}
                    >
                      <Check className="w-4 h-4" />
                      Approve
                    </button>
                    <button
                      onClick={() => rejectSubmissionReview(review.id)}
                      className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 flex items-center justify-center gap-1"
                      data-testid={`reject-submission-review-${review.id}`}
                    >
                      <X className="w-4 h-4" />
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters Section */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Status:</label>
            <select
              value={reviewFilters.status}
              onChange={(e) => {
                const newFilters = { ...reviewFilters, status: e.target.value };
                setReviewFilters(newFilters);
                fetchReviews(newFilters);
              }}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
              data-testid="review-filter-status"
            >
              <option value="all">All Reviews</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="removed">Removed</option>
            </select>
          </div>
          
          {/* Sort By */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Sort venues by:</label>
            <select
              value={reviewFilters.sort_by}
              onChange={(e) => {
                const newFilters = { ...reviewFilters, sort_by: e.target.value };
                setReviewFilters(newFilters);
                fetchReviews(newFilters);
              }}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
              data-testid="review-sort-by"
            >
              <option value="review_count">Most Reviews</option>
              <option value="pending_count">Most Pending</option>
              <option value="avg_rating">Highest Rated</option>
              <option value="venue_name">Venue Name</option>
            </select>
          </div>
          
          {/* Venue Filter (Search) */}
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search venues..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
              data-testid="review-search"
            />
          </div>
          
          {/* Expand/Collapse All */}
          <button
            onClick={() => {
              const allExpanded = reviewVenues.every(v => expandedVenues[v.venue_id]);
              const newExpanded = {};
              reviewVenues.forEach(v => { newExpanded[v.venue_id] = !allExpanded; });
              setExpandedVenues(newExpanded);
            }}
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm flex items-center gap-1 hover:bg-gray-50"
          >
            {reviewVenues.every(v => expandedVenues[v.venue_id]) ? (
              <><ChevronUp className="w-4 h-4" /> Collapse All</>
            ) : (
              <><ChevronDown className="w-4 h-4" /> Expand All</>
            )}
          </button>
          
          {/* Refresh Button */}
          <button
            onClick={() => fetchReviews()}
            disabled={reviewsLoading}
            className="px-3 py-1.5 bg-brand text-white rounded-lg text-sm flex items-center gap-1 hover:bg-brand-dark disabled:opacity-50"
            data-testid="review-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${reviewsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>
      
      {/* Venues with Reviews */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-brand" /> 
            Reviews by Venue ({reviewsTotal} reviews across {reviewVenues.length} venues)
          </h3>
        </div>
        
        {reviewsLoading ? (
          <div className="p-8 text-center text-gray-500">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
            Loading reviews...
          </div>
        ) : reviewVenues.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No reviews found. Click Refresh to load.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredReviewVenues.map((venue) => (
              <div key={venue.venue_id} className="bg-white">
                {/* Venue Header Row - Clickable to expand */}
                <div 
                  className={`px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors ${expandedVenues[venue.venue_id] ? 'bg-gray-50' : ''}`}
                  onClick={() => setExpandedVenues(prev => ({ ...prev, [venue.venue_id]: !prev[venue.venue_id] }))}
                  data-testid={`venue-reviews-row-${venue.venue_id}`}
                >
                  <div className="flex items-center gap-4 flex-1">
                    {/* Expand/Collapse Icon */}
                    <div className={`transition-transform duration-200 ${expandedVenues[venue.venue_id] ? 'rotate-90' : ''}`}>
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    </div>
                    
                    {/* Venue Image */}
                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-200">
                      {venue.venue_image ? (
                        <img src={venue.venue_image} alt={venue.venue_name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <MapPin className="w-5 h-5" />
                        </div>
                      )}
                    </div>
                    
                    {/* Venue Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">{venue.venue_name}</p>
                        <span className="text-xs text-gray-500">{venue.venue_city}</span>
                        {venue.venue_type && (
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">{venue.venue_type}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <span className="text-amber-500">★</span> {venue.avg_rating?.toFixed(1) || '-'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Review Stats */}
                    <div className="flex items-center gap-3">
                      <div className="text-center">
                        <p className="text-lg font-semibold text-gray-900">{venue.review_count}</p>
                        <p className="text-xs text-gray-500">Reviews</p>
                      </div>
                      {venue.pending_count > 0 && (
                        <div className="text-center px-3 py-1 bg-orange-50 rounded-lg">
                          <p className="text-lg font-semibold text-orange-600">{venue.pending_count}</p>
                          <p className="text-xs text-orange-600">Pending</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* View Venue Link */}
                  <a
                    href={`/venue/${venue.venue_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-4 p-2 text-brand hover:bg-brand/10 rounded-lg"
                    onClick={e => e.stopPropagation()}
                    title="View Venue"
                  >
                    <Eye className="w-4 h-4" />
                  </a>
                </div>
                
                {/* Expanded Reviews Section */}
                <AnimatePresence>
                  {expandedVenues[venue.venue_id] && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="bg-gray-50 border-t border-gray-200">
                        {venue.reviews && venue.reviews.length > 0 ? (
                          <div className="divide-y divide-gray-200">
                            {venue.reviews.map((review) => (
                              <div key={review.id} className="px-6 py-4 ml-8 flex items-start gap-4 hover:bg-gray-100 transition-colors">
                                {/* Review Content */}
                                <div className="flex-1 min-w-0">
                                  {/* Contributor Info */}
                                  <div className="flex items-center gap-2 mb-2">
                                    <div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center text-brand font-medium text-sm">
                                      {(review.contributor_name || 'A').charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                      <p className="font-medium text-gray-900 text-sm">
                                        {review.contributor_name || 'Anonymous'}
                                      </p>
                                      <div className="flex items-center gap-2 text-xs text-gray-500">
                                        {review.contributor && (
                                          <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">
                                            {review.contributor_level} · {review.contributor_xp} XP
                                          </span>
                                        )}
                                        <span>{review.created_at ? new Date(review.created_at).toLocaleDateString() : '-'}</span>
                                      </div>
                                    </div>
                                    {/* Status Badge */}
                                    <span className={`ml-auto px-2 py-0.5 rounded text-xs font-medium ${
                                      review.status === 'approved' ? 'bg-green-100 text-green-700' :
                                      review.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                                      review.status === 'removed' ? 'bg-red-100 text-red-700' :
                                      'bg-gray-100 text-gray-600'
                                    }`}>
                                      {review.status || 'legacy'}
                                    </span>
                                  </div>
                                  
                                  {/* Ratings */}
                                  <div className="flex items-center gap-4 mb-2">
                                    <div className="flex items-center gap-1">
                                      <span className="text-amber-500">★</span>
                                      <span className="font-medium">{review.rating?.toFixed(1) || '-'}</span>
                                      <span className="text-xs text-gray-500">Rating</span>
                                    </div>
                                    {review.helpful_count > 0 && (
                                      <div className="flex items-center gap-1 text-green-600">
                                        <span>👍</span>
                                        <span className="font-medium">{review.helpful_count}</span>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Review Text */}
                                  {review.content && (
                                    <p className="text-sm text-gray-700 bg-white rounded-lg p-3 border border-gray-200">
                                      "{review.content}"
                                    </p>
                                  )}
                                </div>
                                
                                {/* Actions */}
                                <div className="flex flex-col gap-1 shrink-0">
                                  {review.status === 'pending' && (
                                    <button
                                      onClick={() => handleReviewStatusChange(review.id, 'approved')}
                                      className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 flex items-center gap-1"
                                      data-testid={`approve-review-${review.id}`}
                                    >
                                      <Check className="w-3 h-3" /> Approve
                                    </button>
                                  )}
                                  {(review.status === 'approved' || !review.status) && (
                                    <button
                                      onClick={() => handleReviewStatusChange(review.id, 'removed')}
                                      className="px-3 py-1.5 bg-orange-500 text-white rounded-lg text-xs font-medium hover:bg-orange-600 flex items-center gap-1"
                                      data-testid={`remove-review-${review.id}`}
                                    >
                                      <X className="w-3 h-3" /> Remove
                                    </button>
                                  )}
                                  {review.status === 'removed' && (
                                    <button
                                      onClick={() => handleReviewStatusChange(review.id, 'approved')}
                                      className="px-3 py-1.5 bg-blue-500 text-white rounded-lg text-xs font-medium hover:bg-blue-600 flex items-center gap-1"
                                      data-testid={`restore-review-${review.id}`}
                                    >
                                      <RefreshCw className="w-3 h-3" /> Restore
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDeleteReview(review.id)}
                                    className="px-3 py-1.5 bg-red-100 text-red-600 rounded-lg text-xs font-medium hover:bg-red-200 flex items-center gap-1"
                                    data-testid={`delete-review-${review.id}`}
                                  >
                                    <Trash2 className="w-3 h-3" /> Delete
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="px-6 py-8 ml-8 text-center text-gray-500">
                            <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p>No reviews match the current filter</p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
